import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDomainBySlug } from "../api/strapi/domains";
import { useLanguage } from "../context";
import { getLocalizedPath } from "../helpers/routes";
import { Layout } from "../components";
import PageHero from "../components/common/PageHero";
import SkeletonImage from "../components/common/SkeletonImage";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { motion } from "framer-motion";

// Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const DomainPage = () => {
  const { slug } = useParams();
  const intl = useIntl();
  const { language } = useLanguage();
  const [domain, setDomain] = useState(null);
  const [domainResult, setDomainResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  useEffect(() => {
    const loadDomain = async () => {
      try {
        setLoading(true);
        const result = await fetchDomainBySlug(slug);
        setDomainResult(result);

        if (!result.ok) {
          if (result.reason !== "NOT_FOUND") {
            setError(intl.formatMessage({ id: "common.contentUnavailable" }));
          }
          setDomain(null);
          return;
        }

        setDomain(result.data);
      } catch (err) {
        console.error("Erreur lors du chargement du domaine:", err);
        setError(intl.formatMessage({ id: "common.contentUnavailable" }));
      } finally {
        setLoading(false);
      }
    };

    loadDomain();
  }, [slug, intl]);

  if (loading) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.loading" }) }}>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#16A34A]"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.unavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <h2 className="text-2xl font-bold mb-2">
              {intl.formatMessage({ id: "common.contentUnavailableTitle" })}
            </h2>
            <p>{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!domain) {
    return (
      <Layout data={{ title: intl.formatMessage({ id: "common.sectionUnavailable" }) }}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <h2 className="text-2xl font-bold mb-2">
              {intl.formatMessage({ id: "common.sectionUnavailable" })}
            </h2>
            <p>{intl.formatMessage({ id: "common.noContent" })}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const baseUrl = process.env.REACT_APP_STRAPI_URL || "http://156.67.104.166:1338";
  const iconUrl = domain.icon?.url ? `${baseUrl}${domain.icon.url}` : null;

  const sections = [
    {
      key: "products",
      title: intl.formatMessage({ id: "domain.products" }),
      items: domain.products || [],
      getLink: (item) => getLocalizedPath(`/info/produits/${item.slug}`, language),
    },
    {
      key: "industries",
      title: intl.formatMessage({ id: "domain.industries" }),
      items: domain.industries || [],
      getLink: (item) => getLocalizedPath(`/info/industries/${item.slug}`, language),
    },
    {
      key: "services",
      title: intl.formatMessage({ id: "domain.services" }),
      items: domain.services || [],
      getLink: (item) => getLocalizedPath(`/info/services/${item.slug}`, language),
    },
    {
      key: "rds",
      title: intl.formatMessage({ id: "domain.rd" }),
      items: domain.rds || [],
      getLink: (item) => getLocalizedPath(`/info/r-and-d/${item.slug}`, language),
    },
    {
      key: "companies",
      title: intl.formatMessage({ id: "domain.companies" }),
      items: domain.companies || [],
      getLink: (item) => getLocalizedPath(`/info/company/${item.slug}`, language),
    },
  ];

  const getMetaImageUrl = (metaImage) => {
    if (!metaImage) return null;
    const raw = metaImage.formats?.large?.url || metaImage.url;
    if (!raw) return null;
    return raw.startsWith("http") ? raw : `${baseUrl}${raw}`;
  };

  return (
    <Layout data={{ title: domain.title }}>
      <div className="min-h-screen bg-white">
        <PageHero
          eyebrow="Africa Systems"
          title={domain.title}
          description={domain.description}
          image={iconUrl}
        >
          <div className="text-sm text-white/80">
            {intl.formatMessage({ id: "domain.totalItems" }, { count: (domain.products?.length || 0) + (domain.industries?.length || 0) + (domain.services?.length || 0) + (domain.rds?.length || 0) + (domain.companies?.length || 0) })}
          </div>
        </PageHero>

        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-12">
          {sections.map((section) => {
            if (!section.items || section.items.length === 0) {
              return null;
            }

            return (
              <motion.div 
                key={section.key} 
                className="mb-16 last:mb-0"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={sectionVariants}
              >
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0a1628] border-l-4 border-[#16A34A] pl-4">
                    {section.title}
                  </h2>
                  <span className="bg-[#16A34A]/10 text-[#16A34A] px-3 py-1 rounded-full text-sm font-semibold">
                    {intl.formatMessage({ id: "domain.count" }, { count: section.items.length })}
                  </span>
                </div>

                <Swiper
                  modules={[Navigation, Pagination, Autoplay]}
                  spaceBetween={24}
                  slidesPerView={1}
                  navigation
                  pagination={{ clickable: true }}
                  breakpoints={{
                    640: { slidesPerView: 2 },
                    1024: { slidesPerView: 3 },
                    1280: { slidesPerView: 4 },
                  }}
                  className="pb-12 !px-2"
                >
                  {section.items.map((item) => {
                    const imageUrl = getMetaImageUrl(item.metaImage);
                    return (
                      <SwiperSlide key={item.documentId || item.id}>
                        <a
                          href={section.getLink(item)}
                          className="group h-full flex flex-col rounded-2xl border border-slate-200 bg-white hover:border-[#16A34A]/40 hover:shadow-xl transition-all duration-300 overflow-hidden"
                        >
                          <SkeletonImage
                            className="h-48"
                            src={imageUrl}
                            alt={item.title}
                            imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            overlay={<div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />}
                          >
                            <div className="w-full h-full flex items-center justify-center text-slate-400 italic text-sm">
                              {intl.formatMessage({ id: "common.noImage" })}
                            </div>
                          </SkeletonImage>
                          <div className="p-6 flex flex-col flex-grow">
                            <h3 className="text-lg font-bold text-[#0a1628] mb-2 group-hover:text-[#16A34A] transition-colors line-clamp-2">
                              {item.title}
                            </h3>
                            {item.metaDescription ? (
                              <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-grow">
                                {item.metaDescription}
                              </p>
                            ) : (
                              <div className="flex-grow" />
                            )}
                            <div className="pt-4 border-t border-slate-100 flex items-center text-[#16A34A] font-semibold text-sm">
                              {intl.formatMessage({ id: "common.readMore", defaultMessage: "En savoir plus" })}
                              <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                              </svg>
                            </div>
                          </div>
                        </a>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default DomainPage;
